import { Button } from "@tokenterminal/core/ui/button/Button"
import { Combobox } from "@tokenterminal/core/ui/combobox/Combobox"
import { filterAndSortItems } from "@tokenterminal/core/ui/combobox/searchableList/SearchableList"
import { Icon } from "@tokenterminal/core/ui/icon/Icon"
import { toDictionary } from "@tokenterminal/core/utils/toDictionary"
import { type PresetColumnConfig } from "@tokenterminal/tt-analytics-api-types/dist/api/projects"
import { dequal } from "dequal"
import useMetricsConfiguration from "hooks/trpc/useMetricsConfiguration"
import { useAtom } from "jotai"
import { debounce } from "lodash"
import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import {
  type FiltersAtomWithUndefinedValues,
  type FiltersAtom,
  type FiltersAtomType,
} from "../ScreenerTable"
import AddFilterIcon from "./AddFilterIcon"
import { operatorsWithLabels } from "./constants"
import { SelectedFilter } from "./SelectedFilter"

const AddFilterButton = ({
  selectedTableColumns,
  handleAddLocalFilter,
  onDeleteKeyDown,
  getColumnConfigForField,
  addFilterButtonRef,
}: {
  selectedTableColumns: {
    id: string
    value: string | undefined
    group: string | undefined
    display: string | undefined
  }[]
  handleAddLocalFilter: (
    column: string,
    defaultValue: number | undefined
  ) => void
  onDeleteKeyDown: () => void
  getColumnConfigForField: (field: string) => PresetColumnConfig | undefined
  addFilterButtonRef: React.RefObject<HTMLButtonElement>
}) => {
  const [isOpen, setIsOpen] = useState(false)
  const [search, setSearch] = useState("")

  const children = useMemo(() => {
    const filteredSelectedTableColumns = filterAndSortItems(
      selectedTableColumns,
      search,
      (column) => `${column.group} ${column.display}`
    )

    return [
      filteredSelectedTableColumns.length > 0 ? (
        filteredSelectedTableColumns.map((column) => (
          <Combobox.Item
            key={column.id}
            id={column.id}
            value={column.value}
            onClick={() => {
              const columnConfig = getColumnConfigForField(column.id)
              const defaultValue =
                columnConfig?.cell_data_type === "trend" ? 0.2 : 10
              handleAddLocalFilter(column.id, defaultValue)
            }}
          >
            <div>
              <span>{column.group}</span>{" "}
              <span className="text-fg-secondary">{column.display}</span>
            </div>
          </Combobox.Item>
        ))
      ) : (
        <Combobox.EmptyState key="no-filters-found">
          No filters found
        </Combobox.EmptyState>
      ),
    ]
  }, [
    selectedTableColumns,
    search,
    getColumnConfigForField,
    handleAddLocalFilter,
  ])

  return (
    <Combobox
      drawerHeaderTitle="Filter by value"
      trigger={
        <Button
          ref={addFilterButtonRef}
          variant="ghost"
          size="small"
          className="text-fg-secondary rounded-lg border border-dashed border-neutral-500/50"
          onPress={() => setIsOpen(!isOpen)}
          onKeyDown={(e) => {
            if (e.key === "Backspace") {
              onDeleteKeyDown()
            }
          }}
        >
          Add filter
          <Icon as={AddFilterIcon} />
        </Button>
      }
      placement="bottom left"
      isOpen={isOpen}
      setIsOpen={setIsOpen}
      triggerRef={addFilterButtonRef}
      search={search}
      setSearch={setSearch}
      closeOnSelect
      shouldFilter={false}
    >
      {children}
    </Combobox>
  )
}

/**
 * Responsible for rendering the list of filters and the "Add filter" button.
 * - Keeps a local copy of filter state for two main reasons:
 *   - Immediate UI updates on filter changes (with optional debouncing for
 *     server persistence)
 *   - Supports undefined filter values (helpful for smoother UX) without
 *     persisting them
 * - Has special keyboard focus management to help navigate between filters when
 *   adding or removing them
 */
export default function ColumnFiltersList({
  filtersAtom,
  columns,
  filtersContainerRef,
}: {
  filtersAtom: FiltersAtomType
  columns: PresetColumnConfig[]
  filtersContainerRef: React.RefObject<HTMLDivElement>
}) {
  const addFilterButtonRef = useRef<HTMLButtonElement>(null)

  // [Data fetching]
  const { data: { data: metricConfigurations = [] } = {} } =
    useMetricsConfiguration()
  const metricConfigurationsBySlug = useMemo(
    () => toDictionary(metricConfigurations, "slug"),
    [metricConfigurations]
  )

  // [State]
  const [persistedFiltersState, setPersistedFiltersState] = useAtom(filtersAtom)
  const [localFilterState, setLocalFilterState] =
    useState<FiltersAtomWithUndefinedValues>(persistedFiltersState)
  const displayFilterState = localFilterState || persistedFiltersState

  // Syncs the local filter state with the persisted filter state
  useEffect(() => {
    setLocalFilterState((prev) => {
      if (!dequal(persistedFiltersState, prev)) {
        return persistedFiltersState
      }
      return prev
    })
  }, [persistedFiltersState])

  // Used to automatically open a filter and focus the input when adding it
  const [autoFocusFilterIndex, setAutoFocusFilterIndex] = useState<
    number | null
  >(null)

  const selectedTableColumns = useMemo(() => {
    return (
      persistedFiltersState.columns
        .filter(
          (column) =>
            column.field !== "chains" &&
            column.field !== "chain" &&
            column.field !== "market_sectors" &&
            !column.sparkline
        )
        .map((column) => {
          const columnConfig = columns.find((c) => c.field === column.field)

          return {
            ...column,
            ...columnConfig,
            field: column.field,
            display: columnConfig?.display || column.display,
            group: columnConfig?.group || column.group,
          }
        })
        .map((column) => ({
          id: column.field,
          value: column.display,
          group: column.group,
          display: column.display,
        }))
        // Temporary fix to hide filterable columns if they don't have a display or group
        // Ran into active_addresses_daily_30d_latest not having this data.
        .filter((column) => column.group || column.display)
    )
  }, [persistedFiltersState.columns, columns])

  const getColumnConfigForField = useCallback(
    (field: string) => {
      return columns.find((c) => c.field === field)
    },
    [columns]
  )

  const selectedFilters: SelectedFilter[] = useMemo(() => {
    return displayFilterState.filters.map((filter) => {
      const columnConfig = getColumnConfigForField(filter.field)
      const column = selectedTableColumns.find((c) => c.id === filter.field)

      return {
        ...column,
        ...columnConfig,
        operator: filter.operator,
        field: filter.field,
        display: columnConfig?.display || column?.display,
        value: filter.value,
      } as SelectedFilter
    })
  }, [
    displayFilterState.filters,
    getColumnConfigForField,
    selectedTableColumns,
  ])

  // When a user deletes a filter, we focus the last filter or the "Add filter"
  const handleFocusLastFilter = useCallback(() => {
    const lastFilter = filtersContainerRef.current?.querySelector(
      "[data-filter-group]:last-of-type"
    )
    if (lastFilter) {
      ;(lastFilter as HTMLElement).focus()
    } else if (addFilterButtonRef.current) {
      addFilterButtonRef.current.focus()
    }
  }, [filtersContainerRef])

  /**
   * Filters out undefined values before persisting to the atom setter.
   */
  const persistFilters = useCallback(
    (newFilterState: FiltersAtomWithUndefinedValues) => {
      const validFilters = newFilterState?.filters.filter(
        (filter) => {
          if (filter.value === undefined) {
            return false
          } else if (
            Array.isArray(filter.value) &&
            filter.value.some((v) => v === undefined)
          ) {
            return false
          }
          return true
        }
        // Casting because typescript doesn't understand filtering undefined values
      ) as FiltersAtom["filters"]

      const sortedRangeValueFilters = validFilters.map((filter) => {
        if (filter.operator === "between") {
          return {
            ...filter,
            value: (filter.value as number[])?.sort((a, b) => a - b),
          }
        }
        return filter
      })

      setPersistedFiltersState((prev) => ({
        ...prev,
        filters: sortedRangeValueFilters,
      }))
    },
    [setPersistedFiltersState]
  )

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedPersistFilters = useCallback(debounce(persistFilters, 1000), [
    persistFilters,
  ])

  const handleAddLocalFilter = useCallback(
    (column: string, defaultValue: number | undefined) => {
      setAutoFocusFilterIndex(displayFilterState.filters.length)
      const newFilterStatetate = {
        ...displayFilterState,
        filters: [
          ...displayFilterState.filters,
          {
            field: column,
            operator: operatorsWithLabels[0].value,
            value: undefined,
          },
        ],
      }
      setLocalFilterState(newFilterStatetate)
    },
    [displayFilterState]
  )

  const handleRemoveFilterByIndex = useCallback(
    (index: number) => {
      const newFilterState = {
        ...displayFilterState,
        filters: displayFilterState.filters.filter((f, i) => i !== index),
      }
      setLocalFilterState(newFilterState)
      persistFilters(newFilterState)
      requestAnimationFrame(() => {
        handleFocusLastFilter()
      })
    },
    [displayFilterState, persistFilters, handleFocusLastFilter]
  )

  const handleUpdateFilterByIndex = useCallback(
    ({
      index,
      newValue,
      debounced,
      persist = true,
    }: {
      index: number
      newValue: Partial<SelectedFilter>
      persist?: boolean
      debounced?: boolean
    }) => {
      const newFilterState = {
        ...displayFilterState,
        filters: displayFilterState.filters.map((f, i) => {
          if (index === i) {
            return { ...f, ...newValue }
          }
          return f
        }),
      }

      setLocalFilterState(newFilterState)
      if (persist) {
        if (debounced) {
          debouncedPersistFilters(newFilterState)
        } else {
          persistFilters(newFilterState)
        }
      }
    },
    [displayFilterState, debouncedPersistFilters, persistFilters]
  )

  return (
    <>
      {selectedFilters.map((filter, index) => (
        <SelectedFilter
          key={index}
          filter={filter}
          index={index}
          handleUpdateFilterByIndex={handleUpdateFilterByIndex}
          handleRemoveFilterByIndex={handleRemoveFilterByIndex}
          autoFocusOnMount={autoFocusFilterIndex === index}
          clearAutoFocusOnMount={() => setAutoFocusFilterIndex(null)}
          metricConfiguration={
            filter.metric_slug
              ? metricConfigurationsBySlug.get(filter.metric_slug)
              : undefined
          }
          setAutoFocusFilterIndex={setAutoFocusFilterIndex}
        />
      ))}
      <AddFilterButton
        selectedTableColumns={selectedTableColumns}
        handleAddLocalFilter={handleAddLocalFilter}
        onDeleteKeyDown={handleFocusLastFilter}
        getColumnConfigForField={getColumnConfigForField}
        addFilterButtonRef={addFilterButtonRef}
      />
    </>
  )
}
